import { ComponentProps } from "react";

import { isSortableOperation } from "@dnd-kit/dom/sortable";
import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";

type DragProviderProps = ComponentProps<typeof DragDropProvider>;
export type OnDragOverEvent = Parameters<
  NonNullable<DragProviderProps["onDragOver"]>
>[0];
export type OnDragEndEvent = Parameters<
  NonNullable<DragProviderProps["onDragEnd"]>
>[0];

function isBelowTarget(event: OnDragOverEvent | OnDragEndEvent): boolean {
  if (!isSortableOperation(event.operation)) {
    return false;
  }

  const { source, target } = event.operation;
  if (!source || !target) return false;
  if (source.type !== "card" || target.type !== "card") return false;

  const sourceGroup =
    source.initialGroup != null ? String(source.initialGroup)
    : source.group != null ? String(source.group)
    : undefined;
  const targetGroup = target.group != null ? String(target.group) : undefined;

  if (
    sourceGroup === undefined ||
    targetGroup === undefined ||
    sourceGroup === targetGroup
  ) {
    return false;
  }

  if (!target.shape) return false;

  const manager = source.manager;
  if (!manager) return false;

  const dragOperation = manager.dragOperation;
  const position =
    dragOperation.shape?.current.center ?? dragOperation.position.current;
  if (!position || typeof target.shape.center?.y !== "number") return false;

  const isBelow = Math.round(position.y) > Math.round(target.shape.center.y);
  return isBelow;
}

export type MoveResult = {
  sourceId: string;
  sourceIndex: number;
  sourceType?: string;
  sourceGroup?: string;
  targetId: string;
  targetIndex: number;
  targetType?: string;
  targetGroup?: string;
  didMove: boolean;
};

export const makeResult = (
  event: OnDragOverEvent | OnDragEndEvent,
  didMove: boolean = true,
): MoveResult => {
  if (!isSortableOperation(event.operation)) {
    return {
      sourceId: "",
      sourceIndex: 0,
      targetId: "",
      targetIndex: 0,
      didMove: false,
    };
  }

  const { source, target } = event.operation;
  const rawTargetIndex = target?.index ?? 0;
  const targetIndex = rawTargetIndex + (isBelowTarget(event) ? 1 : 0);

  const canceled = "canceled" in event && event.canceled;

  return {
    sourceId: String(source?.id ?? ""),
    sourceIndex: source?.initialIndex ?? source?.index ?? 0,
    sourceType: source?.type as string,
    sourceGroup: (source?.initialGroup ?? source?.group) as string,
    targetId: String(target?.id ?? ""),
    targetIndex,
    targetType: target?.type as string,
    targetGroup: target?.group as string,
    didMove: didMove && !canceled,
  };
};

export const moveWithId = <Item,>(
  idKey: string,
  items: Item[],
  event: OnDragOverEvent | OnDragEndEvent,
): Item[] => {
  const mapped = items.map((item) => {
    (item as any).id = item[idKey as keyof Item];
    return item;
  }) as Array<Item & { id: string }>;
  return move(mapped, event).map(({ id: _id, ...item }) => item) as Item[];
};
